import React, { ChangeEvent, useEffect, useState } from 'react'
import {
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CForm,
  CFormCheck,
  CFormInput,
  CFormSelect,
  CFormTextarea,
  CMultiSelect,
  CRow,
  CTabContent,
} from '@coreui/react-pro'
import { useGift } from './hooks'
import { createGift, setGift, submitCreateGift } from './model'
import { muslimCountries } from '../../../../libs/countries'
import { Option } from '@coreui/react-pro/src/components/multi-select/types'
import { uniq } from 'lodash'
import { formatVersionInput, hasAll, isVersionInvalid } from '../utils'
import { Divider } from 'antd'
import { useUnit } from 'effector-react'
import { $countriesList } from '../../../../features/dictionaries/model'
import { transformCountriesToOptions } from '../../../../libs/formatCountries'
import { ApiResult } from '../../../../components/apiResult/ApiResult'
import { GiftTypes } from '../constants'

export const CreateGift = () => {
  const { gift, giftCategories } = useGift()
  const countriesList = useUnit($countriesList)
  const [checkAllMuslimCountries, setCheckAllMuslimCountries] = useState(false)
  const INITIAL_STATE_ERROR = {
    platform_name: false,
    platform_title: false,
    platform_link: false,
  }
  const [errorBonusReview, setErrorBonusReview] = useState(INITIAL_STATE_ERROR)
  const [errorGiftType, setErrorGiftType] = useState(false)
  const [downloadFileError, setDownloadFileError] = useState('')
  const INITIAL_STATE_VERSION_ERROR = {
    versionMinIos: false,
    versionMinAndroid: false,
  }
  const [errorVersion, setErrorVersion] = useState(INITIAL_STATE_VERSION_ERROR)

  const isTypeConversationCrownsUsd =
    gift.giftType === 'GIFT_TYPE_CONVERSION_CROWNS_USD'

  const isTypeBonusCrownsReviewScreenshot =
    gift.giftType === 'GIFT_TYPE_BONUS_CROWNS_REVIEW_SCREENSHOT'

  const isTypeMerchTemplates = gift.giftType === 'GIFT_TYPE_MERCH_TEMPLATES'

  const handleChange = (
    event: ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    let value

    if (event.currentTarget.type === 'checkbox') {
      value = (event.currentTarget as HTMLInputElement).checked
    } else if (event.currentTarget.type === 'file') {
      value = (event.currentTarget as HTMLInputElement).files![0]
    } else {
      value = event.currentTarget.value
    }

    setGift({
      ...gift,
      [event.currentTarget.name]: value,
    })
  }

  useEffect(() => {
    if (hasAll(gift.availableCountries, muslimCountries)) {
      setCheckAllMuslimCountries(true)
    } else {
      setCheckAllMuslimCountries(false)
    }
  }, [gift.availableCountries])

  useEffect(() => {
    if (!isTypeConversationCrownsUsd) {
      gift.conversion = null
    }
  }, [gift.name])
  // Функция валидации группы conversion
  const validateConversion = (conversion: {
    target_amount: number
    min_crowns_amount: number
  }): { valid: boolean; conversionOrNull: typeof conversion | null } => {
    const { target_amount, min_crowns_amount } = conversion

    // Проверка "все или ни одного"
    const allZero = target_amount === 0 && min_crowns_amount === 0
    const anyZero = target_amount === 0 || min_crowns_amount === 0
    if (allZero) return { valid: true, conversionOrNull: null }
    if (anyZero) {
      alert('Если введено одно значение конверсии, нужно заполнить оба')
      return { valid: false, conversionOrNull: conversion }
    }

    if (target_amount < 0.0001 || target_amount > 99) {
      alert('Target Amount должно быть от 0.0001 до 99')
      return { valid: false, conversionOrNull: conversion }
    }
    if (min_crowns_amount < 1 || min_crowns_amount > 9999) {
      alert('Min Crowns Amount должно быть от 1 до 9999')
      return { valid: false, conversionOrNull: conversion }
    }

    return { valid: true, conversionOrNull: conversion }
  }

  const validateBonusReview = () => {
    const errors = {
      platform_name: !gift.bonusReview?.platform_name.trim(),
      platform_title: !gift.bonusReview?.platform_title.trim(),
      platform_link: !gift.bonusReview?.platform_link.trim(),
    }
    setErrorBonusReview(errors)
    const hasError = Object.values(errors).some(Boolean)
    return !hasError
  }

  const handleChangeGiftType = (
    event: ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { value } = event.currentTarget
    setGift({
      ...gift,
      giftType: value,
      bonusReview: {
        platform_name: '',
        platform_title: '',
        platform_link: '',
      },
      ...(value === 'GIFT_TYPE_MERCH_TEMPLATES' ? { price: 0 } : {}),
    })
    setErrorBonusReview(INITIAL_STATE_ERROR)
    setErrorGiftType(false)
    setDownloadFileError('')
  }

  const handleChangeDownloadFile = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.currentTarget.files?.[0]
    if (!file) return
    if (file.type !== 'application/zip' && !file.name.endsWith('.zip')) {
      setDownloadFileError('Допустимый формат файла: zip')
      e.currentTarget.value = ''
      return
    }
    if (file.size > 10 * 1024 * 1024) {
      setDownloadFileError('Максимальный размер файла: 10 МБ')
      e.currentTarget.value = ''
      return
    }
    setDownloadFileError('')
    setGift({ ...gift, downloadUrl: file as any })
  }
  const handleChangeVersion = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.currentTarget

    setErrorVersion((prev) => ({ ...prev, [name]: false }))
    setGift({ ...gift, [name]: formatVersionInput(value) })
  }

  const handleChangeCountries = (selected: Option[]) => {
    setGift({
      ...gift,
      availableCountries: selected.map((item) => item.value as string),
    })
  }
  const handleChangeConversion = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    const parsed = value === '' ? 0 : parseFloat(value)

    setGift({
      ...gift,
      conversion: {
        target_amount:
          name === 'target_amount'
            ? parsed
            : gift.conversion?.target_amount ?? 0,
        min_crowns_amount:
          name === 'min_crowns_amount'
            ? parsed
            : gift.conversion?.min_crowns_amount ?? 0,
      },
    })
  }
  const handleChangeBonusReview = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.currentTarget
    setErrorBonusReview((prev) => ({
      ...prev,
      [name]: false,
    }))
    setGift({
      ...gift,
      bonusReview: {
        ...(gift.bonusReview ?? {
          platform_name: '',
          platform_title: '',
          platform_link: '',
        }),
        [name]: value,
      },
    })
  }

  // В сабмите используем функцию
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    let preparedGift = { ...gift }

    const isGiftTypeInvalid =
      !gift.giftType || gift.giftType === 'GIFT_TYPE_UNKNOWN'
    setErrorGiftType(isGiftTypeInvalid)
    if (isGiftTypeInvalid) return

    if (isTypeConversationCrownsUsd && gift.conversion) {
      const { valid, conversionOrNull } = validateConversion(gift.conversion)
      if (!valid) return
      preparedGift.conversion = conversionOrNull
    } else {
      preparedGift.conversion = null
    }

    if (isTypeBonusCrownsReviewScreenshot) {
      const isValid = validateBonusReview()
      if (!isValid) return
    } else {
      preparedGift.bonusReview = null
    }

    const versionErrors = {
      versionMinIos: isVersionInvalid(gift.versionMinIos),
      versionMinAndroid: isVersionInvalid(gift.versionMinAndroid),
    }
    setErrorVersion(versionErrors)
    if (Object.values(versionErrors).some(Boolean)) return

    submitCreateGift(preparedGift)
  }

  return (
    <div>
      <ApiResult
        effect={createGift}
        successMessage="Gift successfully created"
      />
      <CCard className="mb-4">
        <CCardHeader className="d-sm-flex justify-content-between align-items-center">
          <strong>Create gift</strong>
        </CCardHeader>
        <CCardBody>
          <CTabContent className="pt-3">
            <CForm onSubmit={handleSubmit}>
              <CRow className="mb-3">
                <CCol sm={4}>
                  <CFormInput
                    type="text"
                    id="name"
                    name="name"
                    value={gift.name}
                    onChange={handleChange}
                    label="Name in bd"
                  />
                </CCol>
                <CCol sm={4}>
                  <CFormInput
                    type="text"
                    id="title"
                    name="title"
                    value={gift.title}
                    onChange={handleChange}
                    label="Title"
                  />
                </CCol>
              </CRow>
              <CRow className="mb-3">
                <CCol sm={8}>
                  <CFormTextarea
                    id="description"
                    name="description"
                    value={gift.description}
                    onChange={handleChange}
                    label="Description"
                  />
                </CCol>
              </CRow>
              <Divider />
              <CRow className="mb-3">
                <CCol sm={4}>
                  <CFormInput
                    type="text"
                    id="price"
                    name="price"
                    value={gift.price}
                    onChange={handleChange}
                    label="Price"
                    disabled={isTypeMerchTemplates}
                  />
                </CCol>
                <CCol sm={4}>
                  <CFormInput
                    type="text"
                    id="costPrice"
                    name="costPrice"
                    value={gift.costPrice}
                    onChange={handleChange}
                    label="Cost Price (USD)"
                  />
                </CCol>
              </CRow>
              <Divider />
              <CRow className="mb-3">
                <CCol sm={4}>
                  <CFormInput
                    type="file"
                    id="imageSmall"
                    name="imageSmall"
                    label="Choose small image"
                    onChange={handleChange}
                  />
                </CCol>
                <CCol sm={4}>
                  <CFormInput
                    type="file"
                    id="imageMedium"
                    name="imageMedium"
                    label="Choose medium image"
                    onChange={handleChange}
                  />
                </CCol>
                <CCol sm={4}>
                  <CFormInput
                    type="file"
                    id="imageLarge"
                    name="imageLarge"
                    label="Choose large image"
                    onChange={handleChange}
                  />
                </CCol>
              </CRow>
              <CRow className="mb-3">
                <CCol sm={4}>
                  <CFormSelect
                    label="Category"
                    id="categoryId"
                    name="categoryId"
                    value={gift.categoryId}
                    onChange={handleChange}
                  >
                    {giftCategories.map((giftCategory) => (
                      <option key={giftCategory.id} value={giftCategory.id}>
                        {giftCategory.name}
                      </option>
                    ))}
                  </CFormSelect>
                </CCol>
                <CCol sm={8}>
                  <div className="d-flex flex-row align-items-center">
                    <div className="w-50">
                      <CMultiSelect
                        label="Available countries"
                        id="availableCountries"
                        selectionType="text"
                        virtualScroller
                        options={transformCountriesToOptions(countriesList).map(
                          (country) => ({
                            ...country,
                            selected: gift?.availableCountries?.includes(
                              country.value,
                            ),
                          }),
                        )}
                        onChange={handleChangeCountries}
                      />
                    </div>
                    <div className="w-50 mt-4 mx-2">
                      <CFormCheck
                        id="checkAllMuslimCountries"
                        name="checkAllMuslimCountries"
                        checked={checkAllMuslimCountries}
                        onChange={(e) => {
                          setCheckAllMuslimCountries(e.target.checked)
                          if (e.target.checked) {
                            const generalCountries = [
                              ...gift.availableCountries,
                              ...muslimCountries,
                            ]
                            setGift({
                              ...gift,
                              availableCountries: uniq(generalCountries),
                            })
                          } else {
                            setGift({
                              ...gift,
                              availableCountries:
                                gift.availableCountries.filter(
                                  (item) => !muslimCountries.includes(item),
                                ),
                            })
                          }
                        }}
                        label="Check all muslim countries"
                      />
                    </div>
                  </div>
                </CCol>
              </CRow>
              <CRow className="mb-3">
                <CCol sm={4}>
                  <CFormCheck
                    id="isShow"
                    name="isShow"
                    checked={gift.isShow}
                    onChange={handleChange}
                    label="Availability to order"
                  />
                </CCol>
              </CRow>
              <CRow className="mb-3">
                <CCol sm={4}>
                  <CFormSelect
                    label="Type of gift"
                    id="giftType"
                    name="giftType"
                    value={gift.giftType}
                    onChange={handleChangeGiftType}
                    invalid={errorGiftType}
                    feedback="Выберите тип подарка"
                  >
                    {GiftTypes.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </CFormSelect>
                </CCol>
              </CRow>
              {isTypeMerchTemplates && (
                <CRow className="mb-3">
                  <CCol sm={4}>
                    <CFormInput
                      type="text"
                      id="label"
                      name="label"
                      value={gift.label}
                      onChange={handleChange}
                      label="Label"
                    />
                  </CCol>
                  <CCol sm={4}>
                    <CFormInput
                      type="file"
                      id="downloadUrl"
                      name="downloadUrl"
                      accept=".zip"
                      onChange={handleChangeDownloadFile}
                      label="Download file"
                      invalid={!!downloadFileError}
                      feedback={downloadFileError}
                    />
                  </CCol>
                </CRow>
              )}
              {isTypeBonusCrownsReviewScreenshot && (
                <CRow className="mb-3">
                  <CCol sm={4}>
                    <CFormInput
                      type="text"
                      id="platform_name"
                      name="platform_name"
                      value={gift.bonusReview?.platform_name ?? ''}
                      onChange={handleChangeBonusReview}
                      label="Platform Name"
                      invalid={errorBonusReview.platform_name}
                      feedback="Required field"
                    />
                  </CCol>
                  <CCol sm={4}>
                    <CFormInput
                      type="text"
                      id="platform_title"
                      name="platform_title"
                      value={gift.bonusReview?.platform_title ?? ''}
                      onChange={handleChangeBonusReview}
                      label="Platform Title"
                      invalid={errorBonusReview.platform_title}
                      feedback="Required field"
                    />
                  </CCol>
                  <CCol sm={4}>
                    <CFormInput
                      type="text"
                      id="platform_link"
                      name="platform_link"
                      value={gift.bonusReview?.platform_link ?? ''}
                      onChange={handleChangeBonusReview}
                      label="Platform Link"
                      invalid={errorBonusReview.platform_link}
                      feedback="Required field"
                    />
                  </CCol>
                </CRow>
              )}
              <CRow className="mb-3">
                <CCol sm={4}>
                  <CFormInput
                    type="text"
                    id="availableNumber"
                    name="availableNumber"
                    value={gift.availableNumber}
                    onChange={handleChange}
                    label="Quantity"
                  />
                </CCol>
                <CCol sm={4}>
                  <CFormInput
                    type="text"
                    id="sorting"
                    name="sorting"
                    value={gift.sorting}
                    onChange={handleChange}
                    label="Sorting"
                  />
                </CCol>
              </CRow>
              {isTypeConversationCrownsUsd && (
                <>
                  <Divider />
                  <strong>Conversion</strong>
                  <CRow className="mb-3">
                    <CCol sm={4}>
                      <CFormInput
                        type="number"
                        step="0.0001"
                        name="target_amount"
                        value={gift.conversion?.target_amount ?? ''}
                        onChange={handleChangeConversion}
                        label="Target Amount"
                      />
                    </CCol>
                    <CCol sm={4}>
                      <CFormInput
                        type="number"
                        step="1"
                        name="min_crowns_amount"
                        value={gift.conversion?.min_crowns_amount ?? ''}
                        onChange={handleChangeConversion}
                        label="Min Crowns Amount"
                      />
                    </CCol>
                  </CRow>
                </>
              )}
              <CRow className="mb-3">
                <CCol sm={4}>
                  <CFormInput
                    type="text"
                    id="versionMinIos"
                    name="versionMinIos"
                    className="version-mask-hint"
                    value={gift.versionMinIos}
                    onChange={handleChangeVersion}
                    label="Version min (iOS)"
                    placeholder="1.2.3"
                    invalid={errorVersion.versionMinIos}
                    feedback="Формат версии: x.y.z"
                  />
                </CCol>
              </CRow>
              <CRow className="mb-3">
                <CCol sm={4}>
                  <CFormInput
                    type="text"
                    id="versionMinAndroid"
                    name="versionMinAndroid"
                    className="version-mask-hint"
                    value={gift.versionMinAndroid}
                    onChange={handleChangeVersion}
                    label="Version min (Android)"
                    placeholder="1.2.3"
                    invalid={errorVersion.versionMinAndroid}
                    feedback="Формат версии: x.y.z"
                  />
                </CCol>
              </CRow>
              <CButton className="mb-3" type={'submit'}>
                Save
              </CButton>
            </CForm>
          </CTabContent>
        </CCardBody>
      </CCard>
    </div>
  )
}
